import { APP_CONSTANTS } from "@/config";
import { AI_KEY_MISSING_CODE, AI_KEY_MISSING_NOTICE, isAiKeyMissing } from "./ai-key";

export const COPILOT_UNAVAILABLE_MESSAGE =
  "Serviço de IA indisponível. Tente novamente em alguns instantes.";

export type CopilotUnavailableReason =
  | "connection_failed"
  | "not_configured"
  | "upstream_error"
  | "upstream_auth"
  | "missing_user_token"
  // TENANT-AIKEY-REQUIRED-01: Laravel refused the turn, the clinic has no model key.
  | "ai_key_missing";

export type CopilotUnavailableError = {
  message: string;
  reason: CopilotUnavailableReason | null;
};

const COPILOT_UNAVAILABLE_REASONS = new Set<CopilotUnavailableReason>([
  "connection_failed",
  "not_configured",
  "upstream_error",
  "upstream_auth",
  "missing_user_token",
]);

type ErrorEnvelope = {
  error?: {
    code?: unknown;
    service?: unknown;
    reason?: unknown;
    message?: unknown;
  };
  type?: unknown;
  errorText?: unknown;
};

/** What the thread says when a turn dies mid-stream (SPEC-COPILOT-02, state
 * "Provider 503"). The run was accepted and then failed, so the answer belongs
 * in the conversation with a retry, not in the panel-wide outage banner. */
export const COPILOT_STREAM_ERROR_MESSAGE = "O Copiloto não respondeu. Tente de novo.";

/**
 * True when the failure arrived as the SSE `error` event of an accepted run.
 *
 * The event's own `errorText` is the runtime's English internal message, so it
 * is never shown: the clinic reads `COPILOT_STREAM_ERROR_MESSAGE`. The outage
 * text keeps its banner, so it is excluded here.
 */
export function isCopilotStreamError(error: unknown): boolean {
  const rawMessage = error instanceof Error ? error.message : error;
  if (typeof rawMessage !== "string") return false;

  let body: ErrorEnvelope;
  try {
    body = JSON.parse(rawMessage) as ErrorEnvelope;
  } catch {
    return false;
  }

  if (!body || typeof body !== "object" || body.type !== "error") return false;
  return body.errorText !== COPILOT_UNAVAILABLE_MESSAGE;
}

export function getCopilotUnavailableMessage(error: unknown): CopilotUnavailableError | null {
  const rawMessage = error instanceof Error ? error.message : error;
  if (rawMessage === COPILOT_UNAVAILABLE_MESSAGE) {
    return { message: COPILOT_UNAVAILABLE_MESSAGE, reason: null };
  }

  let body: ErrorEnvelope;
  try {
    body = typeof rawMessage === "string"
      ? JSON.parse(rawMessage) as ErrorEnvelope
      : rawMessage as ErrorEnvelope;
  } catch {
    return { message: COPILOT_UNAVAILABLE_MESSAGE, reason: null };
  }

  // The clinic has no model key of its own: a refusal with its own sentence,
  // never the generic outage copy (TENANT-AIKEY-REQUIRED-01).
  if (isAiKeyMissing(body)) return { message: AI_KEY_MISSING_NOTICE, reason: AI_KEY_MISSING_CODE };

  if (body?.error?.code === "FEATURE_PAUSED") return null;

  // A Laravel envelope (top-level `message`, no `error` object) is a policy
  // answer such as 402 Subscription_required or 429 throttling, not an outage:
  // leave it to the regular error handling instead of showing a retry that
  // cannot succeed.
  if (body && typeof body === "object" && !("error" in body) && typeof (body as { message?: unknown }).message === "string") {
    return null;
  }

  if (
    body?.error?.code === "COPILOT_UNAVAILABLE" &&
    body.error.service === "copilot" &&
    typeof body.error.reason === "string" &&
    COPILOT_UNAVAILABLE_REASONS.has(body.error.reason as CopilotUnavailableReason)
  ) {
    return {
      message: typeof body.error.message === "string"
        ? body.error.message
        : COPILOT_UNAVAILABLE_MESSAGE,
      reason: body.error.reason as CopilotUnavailableReason,
    };
  }

  if (body?.type === "error" && body.errorText === COPILOT_UNAVAILABLE_MESSAGE) {
    return { message: COPILOT_UNAVAILABLE_MESSAGE, reason: null };
  }

  return { message: COPILOT_UNAVAILABLE_MESSAGE, reason: null };
}

/**
 * The runtime's own sentence for a refused attachment (422 unsupported type,
 * 413 too large), passed through by the BFF as a FastAPI `detail` envelope.
 *
 * It is the only body in this path that already says what the clinic should do
 * differently, so it is shown verbatim instead of the generic outage copy.
 */
export function getCopilotMediaRejection(error: unknown): string | null {
  const rawMessage = error instanceof Error ? error.message : error;
  if (typeof rawMessage !== "string") return null;

  let body: unknown;
  try {
    body = JSON.parse(rawMessage);
  } catch {
    return null;
  }

  const envelopes = [body, (body as { detail?: unknown })?.detail, (body as { error?: unknown })?.error];
  for (const envelope of envelopes) {
    if (envelope && typeof envelope === "object") {
      const message = (envelope as { user_message?: unknown }).user_message;
      if (typeof message === "string" && message.trim().length > 0) return message;
    }
  }

  return null;
}

export function getCopilotBffUrl(path: string): string {
  const apiBase = (import.meta.env.VITE_API_URL || "/api/v1").replace(/\/+$/, "");
  return `${apiBase}/copilot/${path.replace(/^\/+/, "")}`;
}

export function getCopilotBffHeaders(
  additional: Record<string, string> = {},
): Record<string, string> {
  const token = localStorage.getItem(
    APP_CONSTANTS.AUTH_ACCESS_TOKEN_LOCAL_STORAGE_KEY,
  );
  const tenantId = localStorage.getItem(
    APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY,
  );

  return {
    ...additional,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantId ? { "X-Tenant": tenantId } : {}),
  };
}
